// import { Directive, HostListener } from '@angular/core'
// import { FormGroupDirective } from '@angular/forms'

// @Directive({
//   selector: '[appBilingualSync]',
//   standalone: false
// })
// export class BilingualSyncDirective {
//   constructor(private formGroupDirective: FormGroupDirective) {}

//   // We switch from valueChanges to @HostListener('focusout')
//   // This triggers when the user leaves the input field (Blur)
//   @HostListener('focusout', ['$event.target'])
//   onBlur(target: HTMLInputElement): void {
//     const form = this.formGroupDirective.form
//     const key = target.getAttribute('formControlName')

//     if (!key || !form) return

//     const control = form.get(key)
//     const value = control?.value?.trim()

//     // Only proceed if the field has a value
//     if (value) {
//       const partnerKey = this.getPartnerKey(key, Object.keys(form.controls))

//       if (partnerKey) {
//         const partnerControl = form.get(partnerKey)!

//         // Sync ONLY if the partner is still empty
//         // This ensures we catch the FULL string once typing is done
//         if (!partnerControl?.value) {
//           partnerControl.setValue(value)
//           // Mark as dirty so the form knows it has changed
//           partnerControl.markAsDirty()
//         }
//       }
//     }
//   }

//   private getPartnerKey(currentKey: string, allKeys: string[]): string | null {
//     // Pattern: title <-> titleAr
//     if (currentKey.endsWith('Ar')) {
//       const enKey = currentKey.replace('Ar', '')
//       return allKeys.includes(enKey) ? enKey : null
//     }
//     const arKeyCandidate = currentKey + 'Ar'
//     if (allKeys.includes(arKeyCandidate)) return arKeyCandidate

//     // Pattern: _ar <-> _en
//     if (currentKey.endsWith('_ar')) return currentKey.replace('_ar', '_en')
//     if (currentKey.endsWith('_en')) return currentKey.replace('_en', '_ar')

//     return null
//   }
// }
import { Directive, HostListener, Input } from '@angular/core'
import { FormGroupDirective } from '@angular/forms'

export interface CustomSyncPair {
  field1: string
  field2: string
}

@Directive({
  selector: '[appBilingualSync]',
  standalone: true
})
export class BilingualSyncDirective {
  /** * Optional: Provide specific pairs that don't follow naming conventions.
   * Example: [{ field1: 'headline', field2: 'arabic_text' }]
   */
  @Input() customPairs: CustomSyncPair[] = []

  constructor(private formGroupDirective: FormGroupDirective) {}

  @HostListener('focusout', ['$event.target'])
  onBlur(eventTarget: EventTarget | null): void {
    const target = eventTarget as HTMLInputElement | null
    const form = this.formGroupDirective.form
    const currentKey = target?.getAttribute('formControlName')

    if (!currentKey || !form) return

    const control = form.get(currentKey)
    const value = control?.value?.trim()

    if (value) {
      const partnerKey = this.findPartner(
        currentKey,
        Object.keys(form.controls)
      )

      if (partnerKey) {
        const partnerControl = form.get(partnerKey)

        // Sync ONLY if the partner is currently empty
        if (partnerControl && !partnerControl.value) {
          partnerControl.setValue(value)
          partnerControl.markAsDirty()
          partnerControl.updateValueAndValidity() // Ensures validation triggers
        }
      }
    }
  }

  private findPartner(currentKey: string, allKeys: string[]): string | null {
    // 1. Check Custom Pairs (Manual mapping)
    if (Array.isArray(this.customPairs)) {
      for (const pair of this.customPairs) {
        if (pair.field1 === currentKey) return pair.field2
        if (pair.field2 === currentKey) return pair.field1
      }
    }

    // 2. Logic for 'name' <-> 'nameAr' (The new request)
    if (currentKey.endsWith('Ar')) {
      const baseName = currentKey.replace('Ar', '')
      // If 'titleAr' exists and 'title' exists, they are partners
      if (allKeys.includes(baseName)) return baseName
    } else {
      const arSuffix = currentKey + 'Ar'
      // If 'title' exists and 'titleAr' exists, they are partners
      if (allKeys.includes(arSuffix)) return arSuffix
    }

    // 3. Logic for 'nameEn' <-> 'nameAr' (The new request)
    if (currentKey.endsWith('En')) {
      const target = currentKey.replace('En', 'Ar')
      if (allKeys.includes(target)) return target
    }
    // (The reverse nameAr -> nameEn is already handled by the logic in step 2 if we check carefully,
    // but we can be explicit here)
    if (currentKey.endsWith('Ar')) {
      const target = currentKey.replace('Ar', 'En')
      if (allKeys.includes(target)) return target
    }

    // 4. The "Old" Logic: Logic for '_en' <-> '_ar'
    if (currentKey.endsWith('_en')) return currentKey.replace('_en', '_ar')
    if (currentKey.endsWith('_ar')) return currentKey.replace('_ar', '_en')

    return null
  }
}
